import React, { useState } from 'react';
import { bool, func, number, object, string } from 'prop-types';
import styled, { withTheme } from 'styled-components';

import { genID } from 'ravenjs/utils/generate';
import { getThemeProps } from 'ravenjs/utils/theme';

import AppSubNavigation from 'components/AppSubNavigation';
import { DEFAULT_COLORS } from 'constants/colors';
import MESSAGES from 'constants/messages';
import { IMPERSONATION_RESTRICTED_NAV_ITEMS } from 'constants/navigation';
import Acl, { accessControl } from 'modules/acl';

import { navItemsNotBeClickable } from 'utils/navigation';
import { isNavigationV2Enabled as isNavV2Enabled } from 'utils/company';
import { ArrowDownIcon, ArrowUpIcon } from '../Icons';

/**
 * Generate a activeClassName for the NavLink
 *
 * @type {string}
 */
const navItemActiveClassName = genID();

const NavItemChild = styled.div`
    &::before {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 0;
        right: 0;
        height: 0;
        border-bottom: solid 0.188rem;
        border-color: ${({ selected, theme }) =>
            selected
                ? getThemeProps('NavItem.active.backgroundColor', DEFAULT_COLORS.ORANGE, { theme })
                : 'transparent'};
        transition: border-bottom 0.15s ease-in-out;
    }
`;

const NavItemStyledCommon = styled.div.attrs(() => ({
    activeClassName: navItemActiveClassName,
}))`
    ${getThemeProps('NavItem.styles')};
    align-items: center;
    background-color: transparent;
    border-radius: 4px;
    cursor: ${({ disabled, id, itemsNotBeClickeable }) => {
        if (disabled) {
            return 'not-allowed';
        } else if (itemsNotBeClickeable.indexOf(id.replace(/ /g, '').toLowerCase()) !== -1) {
            return 'default';
        }
        return 'pointer';
    }};
    display: flex;
    justify-content: center;
    position: relative;
    padding: 0 1.1875rem;
    height: 100%;
    font-size: 0.938rem;
    text-decoration: none !important;
    white-space: nowrap;

    &:hover {
        text-shadow: 0 0 1px ${DEFAULT_COLORS.WHITE};
    }
    &:first-of-type {
        padding-left: 0;
    }
    &:last-of-type {
        margin-right: 0;
    }
`;

const NavItemStyledV1 = styled(NavItemStyledCommon)`
    &:hover,
    &.active {
        &::before {
            border-color: ${({ theme }) =>
                getThemeProps('NavItem.active.backgroundColor', DEFAULT_COLORS.ORANGE, { theme })};
        }
    }
`;

const NavItemStyledV2 = styled(NavItemStyledCommon)`
    font-family: 'Roboto', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0;
    color: ${({ theme }) => getThemeProps('NavItem.styles.color', DEFAULT_COLORS.BLACK, { theme })};

    &:hover ${NavItemChild}::before, &.active ${NavItemChild}::before {
        border-color: ${({ theme }) =>
            getThemeProps('NavItem.active.backgroundColor', DEFAULT_COLORS.ORANGE, { theme })};
    }

    /* NEW: Focus styles for keyboard navigation - matches hover behavior */
    &:focus ${NavItemChild}::before {
        border-color: ${({ theme }) =>
            getThemeProps('NavItem.active.backgroundColor', DEFAULT_COLORS.ORANGE, { theme })};
    }

    /* Focus outline for accessibility */
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK} !important;
        outline-offset: 2px;
    }

    /* Hide outline for mouse users, show for keyboard users */
    &:focus:not(:focus-visible) {
        outline: none;
    }

    &:focus-visible {
        outline: 2px solid ${DEFAULT_COLORS.LINK} !important;
        outline-offset: 2px;
    }

    /* Show underline on focus-visible (keyboard focus) */
    &:focus-visible ${NavItemChild}::before {
        border-color: ${({ theme }) =>
            getThemeProps('NavItem.active.backgroundColor', DEFAULT_COLORS.ORANGE, { theme })};
    }
`;

const NavItemTitleWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const ArrowIconWrapper = styled.span`
    display: flex;
    align-items: center;
    margin-left: 1px;
    margin-top: 3px;
`;

const NavItem = props => {
    const {
        isImpersonatingUser,
        isSubNavVisibleOnDOM,
        onMouseEnter,
        onMouseLeave,
        onSubmenuMouseEnter,
        onSubmenuMouseLeave,
        onClick,
        title,
        selected,
        showSubNav,
        state,
        subnav,
        subNavScrollTop,
        isDesktop,
        navItemRef,
        tabIndex,
        theme,
        keyboardOpened,
        onSubmenuItemClick,
    } = props;

    const isNavigationV2Enabled = isNavV2Enabled();

    const disabled = isImpersonatingUser && IMPERSONATION_RESTRICTED_NAV_ITEMS.includes(state);
    const itemsNotBeClickeable = navItemsNotBeClickable(Acl);

    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnterTracked = () => {
        setIsHovered(true);
        onMouseEnter();
    };

    const handleMouseLeaveTracked = () => {
        setIsHovered(false);
        onMouseLeave();
    };

    const onClickNavItem = () => {
        onClick(title);
    };

    const navItemProps = disabled
        ? {
              title: MESSAGES.RESTRICTED_NAV_ITEM,
          }
        : {
              onMouseEnter: handleMouseEnterTracked,
              onMouseLeave: handleMouseLeaveTracked,
              onClick: onClickNavItem,
          };

    const NavItemStyled = isNavigationV2Enabled ? NavItemStyledV2 : NavItemStyledV1;

    const hasDropdown = subnav && Object.keys(subnav).length > 0;

    // Generate a sanitized id for ARIA labelledby references (spaces break aria-labelledby)
    const navItemAriaId = isNavigationV2Enabled
        ? `nav-item-${(title || '').toLowerCase().replace(/\s+/g, '-')}`
        : undefined;

    const renderArrowIcon = () => {
        if (!isNavigationV2Enabled || !hasDropdown) {
            return null;
        }

        const arrowIconFillColor = getThemeProps('NavItem.styles.color', DEFAULT_COLORS.BLACK, {
            theme,
        });

        return (
            <ArrowIconWrapper data-testid="arrow-icon-wrapper">
                {showSubNav ? (
                    <ArrowUpIcon
                        data-testid="arrow-up-icon"
                        width="12"
                        height="12"
                        fill={arrowIconFillColor}
                        viewBox="0 0 16 16"
                    />
                ) : (
                    <ArrowDownIcon
                        data-testid="arrow-down-icon"
                        width="12"
                        height="12"
                        fill={arrowIconFillColor}
                        viewBox="0 0 16 16"
                    />
                )}
            </ArrowIconWrapper>
        );
    };

    return (
        <NavItemStyled
            id={navItemAriaId || title}
            disabled={disabled}
            itemsNotBeClickeable={itemsNotBeClickeable}
            // Added ref for keyboard navigation
            ref={navItemRef}
            // Added tabIndex for keyboard navigation
            tabIndex={tabIndex}
            role={isNavigationV2Enabled && !isHovered ? 'menuitem' : undefined}
            aria-haspopup={isNavigationV2Enabled && !isHovered && hasDropdown ? 'menu' : undefined}
            aria-expanded={
                isNavigationV2Enabled && !isHovered && hasDropdown ? showSubNav : undefined
            }
            aria-current={isNavigationV2Enabled && !isHovered && selected ? 'page' : undefined}
            {...navItemProps}
        >
            <div style={{ position: 'relative' }}>
                {isNavigationV2Enabled ? (
                    <NavItemTitleWrapper
                        aria-hidden={isNavigationV2Enabled && isHovered ? 'true' : undefined}
                    >
                        {title}
                        {renderArrowIcon()}
                    </NavItemTitleWrapper>
                ) : (
                    title
                )}
                <AppSubNavigation
                    isImpersonatingUser={isImpersonatingUser}
                    state={state}
                    show={showSubNav}
                    nav={props}
                    subnav={subnav}
                    isVisibleOnDOM={isSubNavVisibleOnDOM}
                    scrollTop={subNavScrollTop}
                    onMouseEnter={isNavigationV2Enabled ? onSubmenuMouseEnter : undefined}
                    onMouseLeave={isNavigationV2Enabled ? onSubmenuMouseLeave : undefined}
                    isDesktop={isDesktop}
                    parentNavItemId={navItemAriaId}
                    keyboardOpened={keyboardOpened}
                    onSubmenuItemClick={onSubmenuItemClick}
                />
                <NavItemChild selected={selected} />
            </div>
        </NavItemStyled>
    );
};

NavItem.propTypes = {
    isImpersonatingUser: bool,
    isSubNavVisibleOnDOM: bool,
    onMouseEnter: func,
    onMouseLeave: func,
    onSubmenuMouseEnter: func,
    onSubmenuMouseLeave: func,
    onClick: func,
    selected: bool,
    showSubNav: bool,
    state: string,
    subnav: object,
    subNavScrollTop: number,
    title: string,
    history: object,
    isDesktop: bool,
    navItemRef: func,
    tabIndex: number,
    theme: object,
    keyboardOpened: bool,
    onSubmenuItemClick: func,
};

NavItem.defaultProps = {
    isImpersonatingUser: false,
    isSubNavVisibleOnDOM: false,
    onMouseEnter: () => {},
    onMouseLeave: () => {},
    onSubmenuMouseEnter: undefined,
    onSubmenuMouseLeave: undefined,
    onClick: () => {},
    selected: false,
    showSubNav: false,
    state: null,
    subnav: {},
    subNavScrollTop: 0,
    title: '',
    history: {},
    isDesktop: true,
    navItemRef: undefined,
    tabIndex: undefined,
    theme: {},
    keyboardOpened: false,
    onSubmenuItemClick: undefined,
};

export default accessControl(withTheme(NavItem));
